import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"
import { buildCaptionInput } from "../helpers/caption_input"

// An image / file / sketch field of a timesheet template.
//
// Files go up through Active Storage direct upload as soon as they are picked;
// the form then only carries their signed ids. Nothing here owns a named input:
// the field's values are appended to the form's FormData when it is submitted
// (the `formdata` event), under the name the field has at THAT moment:
//
//   <prefix>[<key>][signed_ids][]          new direct uploads
//   <prefix>[<key>][captions][<signed_id>]  their descriptions (photos and files)
//   <prefix>[<key>][sketch]                the sketch canvas as a data URL
//   removed_upload_ids[]                   saved uploads the user removed
//   upload_captions[<id>]                  changed descriptions of saved uploads
//
// <prefix> follows the closest dynamic-form scope (custom_fields →
// field_attachments, additional_entries[N][custom_fields] →
// additional_entries[N][field_attachments], same for site_allocations), which
// the multi-entry / multi-site cloners already keep up to date. So a cloned or
// re-indexed block needs no renaming here, and a field hidden by its conditions
// sends nothing.
//
// Rows carry an owner id: a block cloned while its source held uploads would
// otherwise submit the same files a second time, so a controller drops every
// row it did not create (or adopt on first connect).
//
// A file only has a signed id once its direct upload finished. Submitting
// before that would silently save the entry without the file, so a submit
// during an upload is held: the submit buttons are disabled, the field says
// that its upload is still running, and the form is submitted again (with the
// same submit button) as soon as the last upload is done. A failed upload
// releases the form without submitting it — the user sees the error and
// decides.
//
// Files over the server's limit (maxSize, ConfigurableTimeSheetUpload::
// MAX_FILE_SIZE) are refused before they are uploaded.
export default class extends Controller {
  static targets = ["existingList", "pendingList", "input", "sketchInput", "error", "waiting"]

  static values = {
    fieldKey: String,
    multiple: Boolean,
    captions: Boolean,
    captionPlaceholder: String,
    captionMaxLength: { type: Number, default: 500 },
    directUploadUrl: String,
    maxSize: { type: Number, default: 26214400 },
    errorTooLarge: String,
    errorUpload: String,
  }

  connect() {
    this.adoptOrResetClone()

    this.form = this.element.closest("form")
    this.boundAppend = (event) => this.appendTo(event.formData)
    this.boundSubmit = (event) => this.holdSubmitWhileUploading(event)
    this.form?.addEventListener("formdata", this.boundAppend)
    this.form?.addEventListener("submit", this.boundSubmit)
  }

  disconnect() {
    this.form?.removeEventListener("formdata", this.boundAppend)
    this.form?.removeEventListener("submit", this.boundSubmit)
    if (this.heldSubmit) this.releaseForm()
  }

  openFileDialog(event) {
    event?.preventDefault()
    if (this.hasInputTarget) this.inputTarget.click()
  }

  selectFiles(event) {
    const files = Array.from(event.target.files || [])
    event.target.value = null
    this.addFiles(this.multipleValue ? files : files.slice(-1))
  }

  dragover(event) {
    event.preventDefault()
  }

  drop(event) {
    event.preventDefault()
    const files = Array.from(event.dataTransfer?.files || [])
    this.addFiles(this.multipleValue ? files : files.slice(-1))
  }

  addFiles(files) {
    this.clearError()
    files.forEach((file) => {
      if (file.size > this.maxSizeValue) {
        this.showError(this.errorTooLargeValue.replace("%{name}", file.name))
        return
      }
      // A single-file field keeps only the newest pick; the server replaces the
      // saved file when the new one arrives.
      if (!this.multipleValue) this.pendingRows().forEach((row) => row.remove())
      this.upload(file)
    })
  }

  upload(file) {
    const row = this.buildRow(file.name, true)
    row.dataset.uploading = "true"
    this.pendingListTarget.appendChild(row)

    new DirectUpload(file, this.directUploadUrlValue).create((error, blob) => {
      delete row.dataset.uploading
      if (error) {
        row.remove()
        this.showError(`${this.errorUploadValue} ${file.name}`)
        if (this.heldSubmit) this.heldSubmit.failed = true
      } else {
        row.dataset.signedId = blob.signed_id
        row.querySelector("[data-role='status']")?.remove()
      }
      this.resumeHeldSubmit()
    })
  }

  removePending(event) {
    event.currentTarget.closest("[data-upload-row]")?.remove()
    this.resumeHeldSubmit()
  }

  uploading() {
    return this.pendingRows().some((row) => row.dataset.uploading === "true")
  }

  // Runs before Turbo's own submit handling, which skips a prevented event.
  holdSubmitWhileUploading(event) {
    if (!this.uploading()) return

    event.preventDefault()
    event.stopImmediatePropagation()
    if (this.heldSubmit) return

    this.heldSubmit = { submitter: event.submitter || null, failed: false }
    this.disabledButtons = Array.from(
      this.form.querySelectorAll("button[type='submit'], input[type='submit']"),
    ).filter((button) => !button.disabled)
    this.disabledButtons.forEach((button) => (button.disabled = true))
    if (this.hasWaitingTarget) this.waitingTarget.classList.remove("hidden")
  }

  resumeHeldSubmit() {
    if (!this.heldSubmit || this.uploading()) return

    const { submitter, failed } = this.heldSubmit
    this.releaseForm()
    if (failed) return

    if (submitter && submitter.form === this.form) {
      this.form.requestSubmit(submitter)
    } else {
      this.form.requestSubmit()
    }
  }

  releaseForm() {
    this.heldSubmit = null
    const buttons = this.disabledButtons || []
    buttons.forEach((button) => (button.disabled = false))
    this.disabledButtons = []
    if (this.hasWaitingTarget) this.waitingTarget.classList.add("hidden")
  }

  // A saved upload is only dropped when the form is saved — until then the row
  // is hidden and its id remembered, so cancelling the edit keeps the file.
  removeExisting(event) {
    const row = event.currentTarget.closest("[data-upload-id]")
    if (!row) return

    row.dataset.removed = "true"
    row.classList.add("hidden")
  }

  appendTo(formData) {
    if (this.hiddenByConditions()) return

    const prefix = this.uploadsPrefix()
    this.pendingRows().forEach((row) => {
      if (!row.dataset.signedId) return

      formData.append(`${prefix}[${this.fieldKeyValue}][signed_ids][]`, row.dataset.signedId)
      const caption = this.captionOf(row)
      if (caption) {
        formData.append(
          `${prefix}[${this.fieldKeyValue}][captions][${row.dataset.signedId}]`,
          caption,
        )
      }
    })

    if (this.hasSketchInputTarget && this.sketchInputTarget.value.startsWith("data:image/")) {
      formData.append(`${prefix}[${this.fieldKeyValue}][sketch]`, this.sketchInputTarget.value)
    }

    if (this.hasExistingListTarget) {
      this.existingListTarget
        .querySelectorAll("[data-upload-id][data-removed='true']")
        .forEach((row) => {
          formData.append("removed_upload_ids[]", row.dataset.uploadId)
        })
      this.existingListTarget
        .querySelectorAll("[data-upload-id]:not([data-removed='true'])")
        .forEach((row) => {
          const input = row.querySelector("[data-role='caption']")
          if (input && input.value.trim() !== (input.dataset.original || "").trim()) {
            formData.append(`upload_captions[${row.dataset.uploadId}]`, input.value.trim())
          }
        })
    }
  }

  captionOf(row) {
    return row.querySelector("[data-role='caption']")?.value.trim() || ""
  }

  uploadsPrefix() {
    const scope = this.element.closest("[data-dynamic-form-name-prefix-value]")
    const prefix = scope?.getAttribute("data-dynamic-form-name-prefix-value") || "custom_fields"
    return prefix.replace(/custom_fields(\]?)$/, "field_attachments$1")
  }

  hiddenByConditions() {
    let node = this.element
    while (node && node !== this.form) {
      if (node.matches?.("[data-dynamic-form-target='field'].hidden")) return true
      node = node.parentElement
    }
    return false
  }

  pendingRows() {
    if (!this.hasPendingListTarget) return []
    return Array.from(this.pendingListTarget.querySelectorAll("[data-upload-row]"))
  }

  // The element is stamped with its controller's owner id, both as a data
  // attribute (copied by cloneNode) and as an expando (not copied). A stamp
  // without the matching expando therefore means this element is a clone of
  // another block: everything it shows belongs to the source and is reset.
  // Server-rendered rows (a re-render after a validation error) carry no owner
  // and are adopted.
  adoptOrResetClone() {
    const stamped = this.element.dataset.owner
    const cloned = Boolean(stamped) && this.element.timesheetUploadOwner !== stamped
    this.ownerId =
      !cloned && stamped ? stamped : `upload-owner-${Math.random().toString(36).slice(2, 10)}`
    this.element.dataset.owner = this.ownerId
    this.element.timesheetUploadOwner = this.ownerId

    this.pendingRows().forEach((row) => {
      if (row.dataset.owner && row.dataset.owner !== this.ownerId) {
        row.remove()
      } else {
        row.dataset.owner = this.ownerId
      }
    })
    if (cloned) this.resetClone()
  }

  resetClone() {
    if (this.hasExistingListTarget) this.existingListTarget.replaceChildren()
    if (this.hasSketchInputTarget) this.sketchInputTarget.value = ""
    this.element.querySelector("[data-sketch-target='previewContainer']")?.classList.add("hidden")
    this.element.querySelector("[data-sketch-target='controls']")?.classList.add("hidden")
    this.element.querySelector("[data-sketch-target='placeholder']")?.classList.remove("hidden")
    this.clearError()
  }

  buildRow(name, uploading) {
    const row = document.createElement("div")
    row.dataset.uploadRow = "true"
    row.dataset.owner = this.ownerId
    row.className = "rounded-lg border border-gray-200 bg-gray-50 px-3 py-2"

    const line = document.createElement("div")
    line.className = "flex items-center justify-between gap-2"
    row.appendChild(line)

    const label = document.createElement("span")
    label.className = "min-w-0 flex-1 truncate text-sm text-gray-900"
    label.textContent = name
    line.appendChild(label)

    if (uploading) {
      const status = document.createElement("span")
      status.dataset.role = "status"
      status.className =
        "h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-green-600 border-t-transparent"
      line.appendChild(status)
    }

    const remove = document.createElement("button")
    remove.type = "button"
    remove.className = "shrink-0 p-1 text-gray-400 hover:text-red-500"
    remove.dataset.action = "click->timesheet-upload-field#removePending"
    remove.innerHTML =
      '<svg class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" /></svg>'
    line.appendChild(remove)

    if (this.captionsValue) {
      const caption = buildCaptionInput({
        placeholder: this.captionPlaceholderValue,
        maxLength: this.captionMaxLengthValue,
      })
      caption.dataset.role = "caption"
      row.appendChild(caption)
    }
    return row
  }

  showError(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  clearError() {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("hidden")
  }
}
